import React, { useEffect, useState } from 'react';
import I18n from 'i18n-js';
import { UnstyledButton } from '@/components/ui/Buttons';
import Popover from '@/components/ui/Popover';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import useFlags from '@/hooks/useFlags';
import Icon from '@/components/ui/Icon';
import StudentsPanel from
  '@/components/admin/SlideShow/PresentMode/Navigator/StudentsPanel/StudentsPanel';

const BUTTON_CLASSES = [
  'tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer',
  'tw-text-white tw-text-base tw-font-tci',
  'tw-rounded tw-px-2 tw-py-1',
  'hover:tw-bg-white/10',
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-white',
].join(' ');

const SR_DEBOUNCE_MS = 1500;

// AIDEV-NOTE: PLAT-16887 — Toolbar label rendered to the left of the End Session button while a
// teacher's live session is active. The visible label updates immediately on every count change;
// the SR-only mirror (`aria-live="polite"`) is debounced to satisfy WCAG 2.1 AA (don't flood
// announcements during rapid join/leave bursts). PLAT-16888 — clicking the label opens the
// StudentsPanel as a popover anchored above this button (Radix handles outside-click + Esc).
const PresentingCount = () => {
  const { teacherPacedMode } = useFlags();
  const activeSessionId = useSlideShowContext(state => state.activeSessionId);
  const closeStudentsPanel = useSlideShowContext(state => state.closeStudentsPanel);
  const connectedStudentCount = useSlideShowContext(state => state.connectedStudentCount);
  const invitedStudentIds = useSlideShowContext(state => state.invitedStudentIds);
  const openStudentsPanel = useSlideShowContext(state => state.openStudentsPanel);
  const studentsPanelOpen = useSlideShowContext(state => state.studentsPanelOpen);
  const userType = useSlideShowContext(state => state.userType);

  const [announcedLabel, setAnnouncedLabel] = useState('');

  // "Presenting to X of Y": Y is the currently-invited count from `current_student_counts`
  const label = I18n.t('presenting_to_students', {
    connected: connectedStudentCount ?? 0,
    total: invitedStudentIds?.length ?? 0,
  });

  useEffect(() => {
    const timer = setTimeout(() => setAnnouncedLabel(label), SR_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [label]);

  if (!teacherPacedMode) return null;
  if (userType === 'Student') return null;
  if (!activeSessionId) return null;

  const handleOpenChange = (open: boolean) => {
    if (open) openStudentsPanel();
    else closeStudentsPanel();
  };

  return (
    <>
      <Popover open={studentsPanelOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <UnstyledButton
            aria-label={label}
            className={BUTTON_CLASSES}
            type="button"
          >
            <Icon height={28} icon="Live" />
            <span aria-hidden="true" className="tw-whitespace-nowrap">{label}</span>
          </UnstyledButton>
        </Popover.Trigger>
        <Popover.Content
          align="start"
          className="tw-p-0"
          // AIDEV-NOTE: PLAT-16888 — `collisionPadding.top` is the gap between the panel's top
          // edge and the viewport. Tweak the number to taste; Radix subtracts it from
          // `--radix-popover-content-available-height`, which the panel reads for its height.
          collisionPadding={{ top: 16 }}
          side="top"
          sideOffset={4}
          width="unset"
        >
          <StudentsPanel />
        </Popover.Content>
      </Popover>
      <span aria-live="polite" className="tw-sr-only">{announcedLabel}</span>
    </>
  );
};

export default PresentingCount;
